/**
 * Supabase hides an existing email when signUp returns a user with no
 * identities, and sometimes returns an "already registered" error instead.
 * Neither case creates a profile or changes the password, so checkout must
 * not wait on the profile trigger.
 */

const MIN_SIGNUP_PASSWORD = 8;

const EXISTING_ACCOUNT_MESSAGE = 'An account with this email already exists. Sign in or reset your password.';

const PROFILE_SETUP_MESSAGE = 'Your account was created. Sign in to finish checkout.';

function errorText(error) {
  return String((error && (error.message || error.msg || error.error_description)) || '').toLowerCase();
}

function errorCode(error) {
  return String((error && (error.code || error.error_code)) || '').toLowerCase();
}

function isExistingAccountSignup(authData, authError) {
  const message = errorText(authError);
  const code = errorCode(authError);
  if (message.includes('already registered') || code === 'user_already_exists' || code === 'email_exists') {
    return true;
  }
  const user = authData && authData.user;
  const identities = user && user.identities;
  return Array.isArray(identities) && identities.length === 0;
}

/**
 * 'existing' stops signup immediately.
 * 'created' is a new auth user and may wait for the profile trigger.
 * 'error' is some other signup failure.
 */
function signupPasswordOk(password) {
  return String(password || '').length >= MIN_SIGNUP_PASSWORD;
}

function splitPersonName(value) {
  const parts = String(value || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return { firstName: '', lastName: '' };
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' '),
  };
}

function signupAccountFields(input) {
  const source = input && typeof input === 'object' ? input : {};
  const name = splitPersonName(source.name);
  const email = String(source.email || '').trim();
  const password = String(source.password || '');
  const market = String(source.market || '').trim();
  const errors = [];
  if (!name.firstName) errors.push('name');
  if (!email) errors.push('email');
  if (!signupPasswordOk(password)) errors.push('password');
  if (!market) errors.push('market');
  return {
    ok: errors.length === 0,
    errors,
    name,
    email,
    password,
    market,
  };
}

function signupAccountStatus(authData, authError) {
  if (isExistingAccountSignup(authData, authError)) return 'existing';
  if (authError) return 'error';
  if (authData && authData.user) return 'created';
  return 'error';
}

module.exports = {
  MIN_SIGNUP_PASSWORD,
  EXISTING_ACCOUNT_MESSAGE,
  PROFILE_SETUP_MESSAGE,
  isExistingAccountSignup,
  signupAccountStatus,
  signupPasswordOk,
  splitPersonName,
  signupAccountFields,
};
